import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { uploadSurveyEvidence } from "@/shared/lib/firebaseStorage";
import { resizeImage } from "@/shared/lib/imageProcessing";
import { toast } from "sonner";

interface Props {
  propertyId: string;
  surveyId: string | null;
  /** Ids of ancestor node sections, outermost first; used for upload paths. */
  nodePath: string[];
  fieldId: string;
  value: string[] | undefined;
  onChange: (next: string[]) => void;
  onOpenPhotos?: (urls: string[], index: number, title: string) => void;
}

export function EvidenceUploader({ propertyId, surveyId, nodePath, fieldId, value, onChange, onOpenPhotos }: Props) {
  const urls = value ?? [];
  const [uploading, setUploading] = useState(0);
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Always-fresh ref into the latest urls so concurrent uploads append
  // instead of clobbering each other.
  const urlsRef = useRef<string[]>(urls);
  urlsRef.current = urls;

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        toast.error("Evidence must be an image");
        return;
      }
      if (!surveyId) {
        toast.error("Survey is not loaded yet — try again in a moment");
        return;
      }
      try {
        setUploading((n) => n + 1);
        const resized = await resizeImage(file);
        const { downloadUrl } = await uploadSurveyEvidence(
          { propertyId, surveyId, nodePath },
          fieldId,
          resized
        );
        onChange([...urlsRef.current, downloadUrl]);
      } catch (err: any) {
        console.error("Evidence upload failed:", err);
        toast.error(err?.message ?? "Evidence upload failed");
      } finally {
        setUploading((n) => n - 1);
      }
    },
    [propertyId, surveyId, nodePath, fieldId, onChange]
  );

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Clipboard paste — scoped to this container so it doesn't steal
  // paste events from other inputs on the page.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      let handled = false;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            void uploadFile(file);
            handled = true;
          }
        }
      }
      if (handled) e.preventDefault();
    };
    node.addEventListener("paste", onPaste);
    return () => node.removeEventListener("paste", onPaste);
  }, [uploadFile]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((f) => void uploadFile(f));
    e.target.value = "";
    containerRef.current?.focus();
  };

  const removeAt = (idx: number) => {
    const next = urls.slice();
    next.splice(idx, 1);
    onChange(next);
    // Note: we do not delete the underlying object from Firebase Storage here.
    // Orphan cleanup is out of scope for this feature.
  };

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="flex flex-col gap-2 outline-none"
    >
      <div className="flex items-center gap-3">
        <label className="text-xs text-muted-foreground w-20 shrink-0">Evidence</label>
        <div className="flex flex-wrap gap-2 flex-1">
          {urls.map((url, idx) => (
            <div key={url + idx} className="relative h-14 w-14 rounded overflow-hidden border border-border/50 group">
              <button
                type="button"
                onClick={() => onOpenPhotos?.(urls, idx, "Evidence")}
                disabled={!onOpenPhotos}
                className="h-full w-full block cursor-zoom-in disabled:cursor-default"
                aria-label="Open evidence photo"
              >
                <img src={url} alt="" className="h-full w-full object-cover" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDeleteIdx(idx);
                }}
                className="absolute top-0 right-0 bg-background/80 rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition"
                aria-label="Remove evidence"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-14 w-14 p-0"
            disabled={uploading > 0}
            onClick={() => fileInputRef.current?.click()}
            aria-label="Add evidence photo"
          >
            <ImagePlus className="h-4 w-4" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={onPick}
          />
        </div>
      </div>
      {uploading > 0 && (
        <p className="text-[11px] text-muted-foreground pl-[5.75rem]">
          Uploading {uploading} image{uploading === 1 ? "" : "s"}…
        </p>
      )}
      <p className="text-[11px] text-muted-foreground pl-[5.75rem]">
        Tip: click into this area and paste (⌘V) an image directly.
      </p>

      <AlertDialog
        open={pendingDeleteIdx !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteIdx(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove evidence photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the photo from this survey item. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingDeleteIdx !== null && urls[pendingDeleteIdx] && (
            <div className="flex justify-center py-2">
              <img
                src={urls[pendingDeleteIdx]}
                alt="Evidence to remove"
                className="h-24 w-24 object-cover rounded border border-border/50"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingDeleteIdx !== null) {
                  removeAt(pendingDeleteIdx);
                  setPendingDeleteIdx(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
